import { DateTime } from 'luxon';

// Generic types that match GraphQL generated types
export interface FlexibleSessionInstance {
  flexibleSessionInstanceId: string;
  sessionDate: any; // GraphQL Date type becomes 'any'
  startTime: any | null; // GraphQL Time type becomes 'any'
  endTime: any | null; // GraphQL Time type becomes 'any'
  name: string | null;
}

// Base session interface - all sessions have these fields
export interface BaseSession {
  sessionId: string;
  name: string;
  startTime: any | null; // GraphQL Time type becomes 'any'
  endTime: any | null; // GraphQL Time type becomes 'any'
}

// Extended session interface with flexible session support
export interface SessionWithFlexibleInstances extends BaseSession {
  isFlexible: boolean;
  currentFlexibleInstances: FlexibleSessionInstance[];
}

/**
 * Find a flexible session instance for a specific activity date
 */
export const findFlexibleInstanceForDate = (
  session: BaseSession & Partial<Pick<SessionWithFlexibleInstances, 'isFlexible' | 'currentFlexibleInstances'>>,
  activityDate?: string
): FlexibleSessionInstance | undefined => {
  if (
    !session.isFlexible ||
    !Array.isArray(session.currentFlexibleInstances) ||
    session.currentFlexibleInstances.length === 0 ||
    !activityDate
  ) {
    return undefined;
  }

  return session.currentFlexibleInstances.find((instance) => {
    const instanceDate =
      typeof instance.sessionDate === 'string'
        ? instance.sessionDate
        : DateTime.fromJSDate(instance.sessionDate).toISODate();
    return instanceDate === activityDate;
  });
};

/**
 * Get the display name for a session, accounting for flexible session instances
 */
export const getFlexibleSessionDisplayName = (
  session: BaseSession & Partial<Pick<SessionWithFlexibleInstances, 'isFlexible' | 'currentFlexibleInstances'>>,
  activityDate?: string
): { displayName: string; isCustom: boolean; originalName?: string } => {
  const flexibleInstance = findFlexibleInstanceForDate(session, activityDate);

  if (flexibleInstance?.name) {
    return {
      displayName: flexibleInstance.name,
      isCustom: true,
      originalName: session.name,
    };
  }

  return {
    displayName: session.name,
    isCustom: false,
  };
};

/**
 * Get the display times for a session, accounting for flexible session instances
 */
export const getFlexibleSessionDisplayTimes = (
  session: BaseSession & Partial<Pick<SessionWithFlexibleInstances, 'isFlexible' | 'currentFlexibleInstances'>>,
  activityDate?: string,
  timeStringFunc: (time: string) => string = (time) => time
): string => {
  const flexibleInstance = findFlexibleInstanceForDate(session, activityDate);

  if (flexibleInstance && flexibleInstance.startTime && flexibleInstance.endTime) {
    // Use the flexible instance times if available
    return `${timeStringFunc(flexibleInstance.startTime)} - ${timeStringFunc(flexibleInstance.endTime)}`;
  }

  // Otherwise use the default session times
  return `${timeStringFunc(session.startTime)} - ${timeStringFunc(session.endTime)}`;
};

/**
 * Check if a session has flexible customization for a specific date
 */
export const hasFlexibleCustomizationForDate = (
  session: BaseSession & Partial<Pick<SessionWithFlexibleInstances, 'isFlexible' | 'currentFlexibleInstances'>>,
  activityDate?: string
): boolean => {
  const flexibleInstance = findFlexibleInstanceForDate(session, activityDate);
  return !!(flexibleInstance && (flexibleInstance.name || flexibleInstance.startTime || flexibleInstance.endTime));
};